/**
 * Live "this is the menu they will get" preview for the Admin Users editor.
 * Mirrors the real sidebar (same NAV source, same permission rules) so the
 * master admin can see the effect of a toggle before saving it.
 */
import React from 'react';
import { Eye, LogIn, AlertTriangle } from 'lucide-react';
import { NAV, canSeeNavItem, canOpenPath, firstAllowedPath } from '../../../components/admin/AdminLayout';

// Permissions that grant an ability but never add a menu row of their own.
const SILENT_ABILITIES = {
  can_view_invoices: 'Can look at GST invoices and eWarranties (read-only)',
  can_post_payroll: 'Can post payroll into expenses',
  can_manage_expenses: 'Can create and edit expenses',
  can_manage_inventory: 'Can add stock, consume and restock',
  can_manage_billing_settings: 'Can edit billing setup',
};

// Granted but has no visible effect anywhere in the console.
const NO_EFFECT = {
  can_view_activity_log: 'Activity Log is master-admin only, so this grant does nothing',
};

const MenuPreview = ({ role, permissions }) => {
  const previewUser = {
    role: role || 'admin',
    is_master_admin: false,
    permissions: permissions || {},
  };
  const isStaff = previewUser.role === 'staff';

  const rows = [];
  let pendingGroup;
  NAV.forEach((item, i) => {
    if (item.group !== undefined) { pendingGroup = { group: item.group, key: `g${i}` }; return; }
    const visible = canSeeNavItem(item, previewUser)
      && (!isStaff || canOpenPath(item.to, previewUser));
    if (!visible) return;
    // Group labels (and the plain divider) only appear when something
    // visible actually sits under them — same as the real sidebar.
    if (pendingGroup) { rows.push(pendingGroup); pendingGroup = undefined; }
    rows.push({ ...item, key: item.testid });
  });

  const landing = isStaff
    ? (permissions?.can_self_mark_attendance
        ? '/admin/my-attendance'
        : (['/admin/my-performance', '/admin/settings/password'].includes(firstAllowedPath(previewUser))
            ? '/staff'
            : firstAllowedPath(previewUser)))
    : firstAllowedPath(previewUser);
  const landingLabel = (NAV.find((n) => n.to === landing) || {}).label
    || (landing === '/staff' ? 'Staff portal' : landing);

  const granted = Object.keys(permissions || {}).filter((k) => permissions[k]);
  const abilities = granted.filter((k) => SILENT_ABILITIES[k]);
  const dead = granted.filter((k) => NO_EFFECT[k]);

  return (
    <div data-testid="menu-preview">
      <h3 className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
        <Eye size={13} /> Menu preview
      </h3>

      <div className="admin-sidebar-dark rounded-xl bg-slate-950 p-3 shadow-inner" data-testid="menu-preview-sidebar">
        <div className="px-2 pb-2.5 mb-1 border-b border-white/10">
          <div className="text-[13px] font-semibold text-white leading-tight">Unisol Global</div>
          <div className="text-[9px] uppercase tracking-[0.14em] text-slate-500">Admin console</div>
        </div>

        {rows.length === 0 ? (
          <p className="px-2 py-6 text-center text-xs text-slate-500" data-testid="menu-preview-empty">
            No modules yet — this login would only see the sign-out button.
          </p>
        ) : rows.map((r, idx) => (
          r.group !== undefined ? (
            r.group === null ? <div key={r.key} className="h-2" /> : (
              <div key={r.key} className="flex items-center gap-1.5 px-1 pt-3 pb-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-teal-300">
                <span className="h-px flex-1 bg-white/10" />
                <span>{r.group}</span>
                <span className="h-px flex-1 bg-white/10" />
              </div>
            )
          ) : (
            <div
              key={r.key}
              className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] ${
                idx === 0 ? 'bg-teal-500/15 text-teal-200' : 'text-slate-300'
              }`}
              data-testid={`preview-${r.testid}`}
            >
              <r.icon size={14} className="shrink-0 opacity-80" />
              <span className="truncate">{r.label}</span>
            </div>
          )
        ))}
      </div>

      <div className="mt-2.5 flex items-start gap-1.5 text-xs text-slate-600" data-testid="menu-preview-landing">
        <LogIn size={13} className="mt-0.5 shrink-0 text-teal-600" />
        <span>Lands on <b className="text-slate-800">{landingLabel}</b> after signing in</span>
      </div>

      {abilities.length > 0 && (
        <div className="mt-2.5" data-testid="menu-preview-abilities">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Also allowed</div>
          <div className="flex flex-wrap gap-1">
            {abilities.map((k) => (
              <span key={k} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
                {SILENT_ABILITIES[k]}
              </span>
            ))}
          </div>
        </div>
      )}

      {dead.length > 0 && (
        <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-600" data-testid="menu-preview-no-effect">
          <AlertTriangle size={13} className="mt-0.5 shrink-0 text-slate-400" />
          <span>{dead.map((k) => NO_EFFECT[k]).join('. ')}.</span>
        </div>
      )}
    </div>
  );
};

export default MenuPreview;
